// Made by Mouses — custom scripts

document.addEventListener('DOMContentLoaded', function () {
  initGiftWrap();
  initFreeGift();
  initDefaultAvailabilityFilter();
});

// Shared Cart AJAX helpers for the cart features below.
const fetchCart = () =>
  fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((response) => {
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  });

// Section ids of every rendered cart-items component, for the `sections` param of Cart AJAX calls.
const cartSectionIds = () =>
  Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);

async function initDefaultAvailabilityFilter() {
  const PARAM = 'filter.v.availability';
  const STORAGE_KEY = 'mbmAvailabilityFilterRemoved';

  if (!location.pathname.includes('/collections/')) return;
  if (!document.querySelector(`facets-form-component input[name="${PARAM}"]`)) return;

  const hasParam = () => new URLSearchParams(location.search).has(PARAM);
  let removedBySession = false;
  try { removedBySession = sessionStorage.getItem(STORAGE_KEY) === '1'; } catch (error) { /* storage unavailable */ }

  if (!hasParam() && !removedBySession) {
    const url = new URL(location.href);
    url.searchParams.set(PARAM, '1');
    location.replace(url.toString());
    return;
  }

  const trackRemoval = () => {
    try { sessionStorage.setItem(STORAGE_KEY, hasParam() ? '0' : '1'); } catch (error) { /* storage unavailable */ }
  };
  const { StandardEvents } = await import('@shopify/events');
  document.addEventListener(StandardEvents.collectionUpdate, trackRemoval);
  window.addEventListener('popstate', trackRemoval);
}

// PDP gift wrap checkbox + option dropdown (blocks/gift-wrap.liquid); on add-to-cart the chosen wrap variant is added as a separate line item with the same quantity.
async function initGiftWrap() {
  const giftWrapElements = document.querySelectorAll('.gift-wrap');
  if (!giftWrapElements.length) return;

  const [{ CartLinesUpdateEvent, StandardEvents }, { fetchConfig }] = await Promise.all([
    import('@shopify/events'),
    import('@theme/utilities'),
  ]);

  giftWrapElements.forEach((element) => {
    const checkbox = element.querySelector('input[type="checkbox"]');
    const select = element.querySelector('.gift-wrap__select');
    if (!checkbox || !select) return;

    checkbox.addEventListener('change', () => {
      select.hidden = !checkbox.checked;
    });

    const addGiftWrap = async (quantity) => {
      const variantId = Number(select.value);
      if (!variantId) return;

      const deferredEventPromise = CartLinesUpdateEvent.createPromise();

      element.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity }],
          promise: deferredEventPromise.promise,
        })
      );

      try {
        // Visible "Voor" tells the gift wrapper which product the wrap belongs to; hidden
        // "_voor" (product id) is what the cart sort matches on. Distinct properties also
        // keep wraps for different products on separate cart lines.
        const item = { id: variantId, quantity };
        if (element.dataset.productId) {
          item.properties = { Voor: element.dataset.productTitle, _voor: element.dataset.productId };
        }

        const body = JSON.stringify({ items: [item], sections: cartSectionIds().join(',') });
        const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body })).then((result) =>
          result.json()
        );

        const cart = await fetchCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'gift-wrap',
            itemCount: quantity,
            sections: response.sections,
            didError: Boolean(response.status),
          },
        });
      } catch (error) {
        console.error('[gift-wrap] Failed to add gift wrap to cart:', error);
        deferredEventPromise.reject(error);
      }
    };

    // Watch cart additions from this product's form and append the gift wrap line.
    // The self-dispatched event above never matches: its target is not inside a product form.
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (!checkbox.checked || !select.value) return;
      if (event.action && event.action !== 'add') return;

      const productForm = event.target instanceof Element ? event.target.closest('product-form-component') : null;
      if (!productForm) return;
      // Only react to the product form in the same section as this gift wrap block.
      if (!element.closest('.shopify-section')?.contains(productForm)) return;

      const quantityInput = productForm.querySelector('form input[name="quantity"]');
      const quantity = Number(quantityInput?.value) || 1;

      event.promise
        ?.then(({ detail }) => {
          if (detail?.didError) return;
          addGiftWrap(quantity);
        })
        .catch(() => {});
    });
  });
}

// Cart free gift (snippets/mbm-free-gift.liquid, PSIKHOU-1562). Checkbox adds/removes the
// gift variant via the Cart AJAX API with a `_mbm_free_gift` line item property. After every
// cart mutation a reconcile pass removes gift lines that no longer match the offered
// threshold (subtotal dropped below it, or a higher threshold now offers a different gift).
async function initFreeGift() {
  const PROP = '_mbm_free_gift';
  const [{ CartLinesUpdateEvent, StandardEvents }, { fetchConfig }] = await Promise.all([
    import('@shopify/events'),
    import('@theme/utilities'),
  ]);

  // All cart mutations run through one queue so rapid toggling can't interleave (AC 4.6).
  let queue = Promise.resolve();
  const enqueue = (task) => {
    queue = queue.then(task, task);
    return queue;
  };

  // Perform a mutation, then notify the theme so cart sections morph. `mutate` resolves with
  // `{ cart, sections }`; when the endpoint does not return the full cart (`/cart/add.js`),
  // `cart` is omitted and fetched here.
  const mutateCart = async (action, mutate) => {
    const target = document.querySelector('.mbm-free-gift') || document.documentElement;
    const deferred = CartLinesUpdateEvent.createPromise();
    target.dispatchEvent(
      new CartLinesUpdateEvent({ action, context: 'cart', lines: [], promise: deferred.promise })
    );
    try {
      const result = (await mutate()) || {};
      const cart = result.cart || (await fetchCart());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'mbm-free-gift', itemCount: cart.item_count, sections: result.sections },
      });
    } catch (error) {
      console.error('[mbm-free-gift] Cart mutation failed:', error);
      deferred.reject(error);
      throw error;
    }
  };

  // Every line carrying the gift property is managed by this system, paid or not:
  // reconcile removes lines whose discount was dropped instead of leaving them behind.
  const giftLines = (items) => items.filter((item) => item.properties && item.properties[PROP]);

  // One `/cart/update.js` call removes all keys; the response is the full cart plus sections.
  const removeLineKeys = async (keys) => {
    const body = JSON.stringify({
      updates: Object.fromEntries(keys.map((key) => [key, 0])),
      sections: cartSectionIds().join(','),
    });
    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body })).then((result) =>
      result.json()
    );
    if (response.status) throw new Error(response.description || 'Cart update failed');
    return { cart: response, sections: response.sections };
  };

  const addGift = (variantId, thresholdCents) =>
    mutateCart('add', async () => {
      // Max one gift line at a time: clear any existing gift lines first (AC 4.6).
      const existing = giftLines((await fetchCart()).items).map((line) => line.key);
      if (existing.length) await removeLineKeys(existing);
      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { [PROP]: thresholdCents } }],
        sections: cartSectionIds().join(','),
      });
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body })).then((result) =>
        result.json()
      );
      if (response.status) throw new Error(response.description || 'Add to cart failed');
      return { sections: response.sections };
    });

  const removeGift = () =>
    mutateCart('remove', async () => {
      const keys = giftLines((await fetchCart()).items).map((line) => line.key);
      if (!keys.length) return null;
      return removeLineKeys(keys);
    });

  // Remove gift lines that no longer belong: subtotal below their threshold, a different
  // gift now offered, or duplicates (AC 4.7 / 4.8). Never auto-adds a gift.
  // `knownItems` is the cart snapshot from the triggering event: when it holds no gift line
  // there is nothing to reconcile, so the common case costs no request.
  const reconcile = (knownItems) =>
    enqueue(async () => {
      if (knownItems && !giftLines(knownItems).length) return;
      const cart = await fetchCart();
      const gifts = giftLines(cart.items);
      if (!gifts.length) return;

      const block = document.querySelector('.mbm-free-gift');
      const thresholds = (block?.dataset.thresholds || '')
        .split(',')
        .filter(Boolean)
        .map((pair) => {
          const [cents, variantId] = pair.split(':');
          return { cents: Number(cents), variantId: Number(variantId) };
        });

      const eligibleTotal = cart.items.reduce((total, item) => {
        if (item.properties && item.properties[PROP]) return total;
        if (item.gift_card) return total;
        return total + item.final_line_price;
      }, 0);

      const offered = thresholds
        .filter((threshold) => threshold.cents <= eligibleTotal)
        .sort((a, b) => b.cents - a.cents)[0];

      let keptOfferedLine = false;
      const staleKeys = gifts
        .filter((line) => {
          const matches = offered && line.variant_id === offered.variantId && line.final_line_price === 0 && !keptOfferedLine;
          if (matches) keptOfferedLine = true;
          return !matches;
        })
        .map((line) => line.key);

      if (!staleKeys.length) return;
      // Already logged by mutateCart; nothing to roll back for a background reconcile.
      await mutateCart('remove', () => removeLineKeys(staleKeys)).catch(() => {});
    });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'mbm-free-gift-toggle') return;
    const blockElement = input.closest('.mbm-free-gift');
    if (!blockElement) return;

    const variantId = Number(blockElement.dataset.variantId);
    const thresholdCents = blockElement.dataset.thresholdCents;
    input.disabled = true; // re-enabled by the server-rendered morph
    const mutation = input.checked ? () => addGift(variantId, thresholdCents) : () => removeGift();
    enqueue(() =>
      mutation().catch(() => {
        // Mutation failed: no morph will re-render the block, so restore the checkbox.
        input.disabled = false;
        input.checked = !input.checked;
      })
    );
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    // Skip our own mutations: they dispatch from the gift block itself, and reconcile
    // already runs queued after them. (`source` lives in the promise payload, not on
    // event.detail, so it can't be used as a filter here.)
    if (event.target instanceof Element && event.target.closest('.mbm-free-gift')) return;
    event.promise ? event.promise.then((payload) => reconcile(payload?.detail?.items), () => {}) : reconcile();
  });
}

// PLP/PDP delivery time (blocks/delivery-time.liquid). Custom element so cards added by
// filtering/pagination re-init; logic ported from the old theme's mbm-shipping-notification.js.
customElements.define(
  'delivery-time',
  class DeliveryTime extends HTMLElement {
    connectedCallback() {
      const threshold = 15; // order cut-off hour for next-day delivery
      const now = new Date();
      const day = now.getDay();
      const beforeThreshold = now.getHours() < threshold;
      // Weekend window: Friday from the cut-off hour through Sunday.
      const weekend = day === 0 || day === 6 || (day === 5 && !beforeThreshold);

      let desktopText;
      let mobileText;
      if (weekend) {
        desktopText = 'Nu besteld, <span>dinsdag in huis</span>';
        mobileText = '<span>dinsdag in huis</span>';
      } else if (beforeThreshold) {
        desktopText = `Voor ${threshold}.00 besteld, <span>morgen in huis</span>`;
        mobileText = '<span>morgen in huis</span>';
      } else {
        desktopText = 'Vandaag besteld, <span>morgen verzonden</span>';
        mobileText = '<span>morgen verzonden</span>';
      }

      this.querySelector('.delivery-time__text--desktop').innerHTML = desktopText;
      this.querySelector('.delivery-time__text--mobile').innerHTML = mobileText;
    }
  }
);
